import { FunctionComponent, SVGProps } from 'react';
import { UserGroup, getRoundProductGroups } from '@round/api';
import { FeatureFlagName } from 'utility/featureFlags';
import { ReactComponent as MicrowaveShortIcon } from 'assets/brand/MicrowaveShort.svg';

const TRNDS_URL = !import.meta.env.PROD ? import.meta.env.VITE_TRNDS_URL : 'https://app.trnds.xyz/';
export const MICROWAVE_MANAGEMENT_URL = !import.meta.env.PROD
    ? import.meta.env.VITE_MICROWAVE_MANAGEMENT_URL ?? ''
    : 'https://micro-wav.thisisround.com/';
const PROJECT_MANAGEMENT_URL = !import.meta.env.PROD
    ? import.meta.env.VITE_PROJECT_MANAGEMENT_URL
    : 'https://back.thisisround.co.uk/project-management/projects/';
const FINANCE_DASHBOARD_URL = !import.meta.env.PROD
    ? import.meta.env.VITE_FINANCE_DASHBOARD_URL
    : 'https://back.thisisround.co.uk/admin/post-payment-requests/dashboard/';

export type NavBarItemGroup = {
    title: string;
    items: NavBarLink[];
    icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
};

export type NavBarLink = {
    title: string;
    groups: UserGroup[];
    url: string;
    openInSameTab?: boolean;
    shouldMatch?: 'all' | 'any';
    featureFlag?: FeatureFlagName;
    icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
};

export type NavBarItem = NavBarItemGroup | NavBarLink;

export const isNavBarItemGroup = (item: NavBarItem): item is NavBarItemGroup =>
    Array.isArray((item as NavBarItemGroup)?.items);

export const NAVBAR_ITEMS: NavBarItemGroup[] = [
    {
        title: 'Advertising',
        items: [
            {
                title: 'Plans',
                shouldMatch: 'any',
                groups: getRoundProductGroups(['plan_viewer']),
                url: '/campaigns/',
            },
            {
                title: 'Projects',
                groups: getRoundProductGroups(['user_admin']),
                url: `${PROJECT_MANAGEMENT_URL}`,
                openInSameTab: true,
            },
        ],
    },
    {
        title: 'Explore',
        items: [
            {
                title: 'Tiktok Influencer Wide Search',
                groups: ['user_admin', 'wide_search_trial'],
                shouldMatch: 'any',
                url: '/influencer/tiktok/search/',
            },
            {
                title: 'Tiktok Artist',
                groups: getRoundProductGroups(['explorer_tiktok_viewer', 'user_admin']),
                url: '/artist/tiktok/list/',
            },
            {
                title: 'Tiktok Influencer',
                groups: getRoundProductGroups(['explorer_tiktok_viewer', 'plan_viewer', 'user_admin']),
                url: '/influencer/tiktok/list/',
            },
            {
                title: 'Tiktok Audio [TRNDS]',
                groups: ['trnds_product', 'user_admin', 'explorer_tiktok_viewer'],
                url: `${TRNDS_URL}explorer/tiktok/audio/`,
            },
            {
                title: 'Tiktok Hashtag',
                groups: getRoundProductGroups(['explorer_tiktok_viewer', 'user_admin']),
                url: '/explorer/tiktok/hashtag/',
            },
            {
                title: 'Instagram Influencer Wide Search',
                groups: ['user_admin', 'wide_search_trial'],
                shouldMatch: 'any',
                url: '/influencer/instagram/search/',
            },
            {
                title: 'Instagram Artist',
                groups: getRoundProductGroups(['explorer_instagram_viewer', 'user_admin']),
                url: '/artist/instagram/list/',
            },
            {
                title: 'Instagram Influencer',
                groups: getRoundProductGroups(['explorer_instagram_viewer', 'plan_viewer', 'user_admin']),
                url: '/influencer/instagram/list/',
            },
        ],
    },
    {
        title: 'Microwave',
        items: [
            {
                title: 'Payments',
                groups: ['microwave_finance', 'microwave_admin'],
                shouldMatch: 'any',
                url: '/microwave/payments/',
            },
            {
                title: 'Campaigns',
                groups: ['microwave_admin'],
                url: `${MICROWAVE_MANAGEMENT_URL}campaigns`,
                openInSameTab: true,
                icon: MicrowaveShortIcon,
            },
            {
                title: 'Influencers',
                groups: ['microwave_admin'],
                url: `${MICROWAVE_MANAGEMENT_URL}influencers`,
                openInSameTab: true,
                icon: MicrowaveShortIcon,
            },
            {
                title: 'Approvals',
                groups: ['microwave_admin'],
                url: `${MICROWAVE_MANAGEMENT_URL}approvals`,
                openInSameTab: true,
                icon: MicrowaveShortIcon,
            },
        ],
    },
    {
        title: 'Finance',
        items: [
            {
                title: 'Invoice Requests',
                groups: ['round_planner'],
                url: '/finance/invoice-requests/',
            },
            {
                title: 'Invoice Tracker',
                groups: getRoundProductGroups(['user_admin', 'finance_viewer']),
                url: '/finance/invoice-tracker/',
            },
            {
                title: 'Post Payments',
                groups: ['round_finance', 'round_planner'],
                shouldMatch: 'any',
                url: '/finance/payments/',
            },
            {
                title: 'Finance Dashboard',
                groups: ['round_finance'],
                url: `${FINANCE_DASHBOARD_URL}`,
                openInSameTab: true,
            },
        ],
    },
    {
        title: 'Admin',
        items: [
            {
                title: 'Client Users',
                groups: getRoundProductGroups(['round_super_admin']),
                url: '/admin/round-client-users/',
            },
            {
                title: 'Facebook Ad Accounts',
                groups: getRoundProductGroups(['user_admin', 'facebook_api_editor']),
                url: '/admin/facebook-ad-account/',
            },
            {
                title: 'Brands',
                groups: getRoundProductGroups(['advertising_editor']),
                url: '/admin/brand/',
            },
            {
                title: 'Currencies',
                groups: getRoundProductGroups(['user_admin']),
                url: '/admin/currency/',
            },
            {
                title: 'Table Validations',
                groups: getRoundProductGroups(['user_admin']),
                url: '/admin/table-validation/',
            },
            {
                title: 'Data Upload',
                groups: getRoundProductGroups(['user_admin']),
                url: '/admin/data-upload/',
            },
            {
                title: 'Data Download',
                groups: ['round_super_admin'],
                url: '/admin/data-download/',
            },
            {
                title: 'Outstanding Items',
                groups: getRoundProductGroups(['user_admin']),
                url: '/admin/outstanding-items/',
            },
            {
                title: 'Report Items',
                groups: getRoundProductGroups(['user_admin']),
                url: '/admin/report-items/',
            },
        ],
    },
];
